(() => {
  const SIZE_INDEX = { small: 0, medium: 1, large: 2 };
  const STORAGE_KEY = 'mini-mint-hoard-builder-v2';
  const SIZE_LABELS = { small: 'small', medium: 'medium', large: 'large' };
  const DEFAULT_LINES = {
    coins: { type: 'mixed', size: 'small', quantity: 1 },
    gems: { type: 'mixed', size: 'small', quantity: 1 },
    ingots: { type: 'mixed', size: 'small', quantity: 1 },
    'trade-bars': { type: 'gold', size: 'small', quantity: 1 },
  };

  const COIN_HANDLES = {
    mixed: [
      '100-mixed-metal-rpg-coins',
      '500-mixed-fantasy-rpg-coins',
      '1000-mixed-metal-rpg-coins',
    ],
    gold: ['100-gold-rpg-coins', '500-gold-rpg-coins', '1000-gold-rpg-coins'],
    dwarven: [
      '100-gold-dwarven-rpg-coins',
      '500-gold-dwarven-rpg-coins',
      '1000-gold-dwarven-rpg-coins',
    ],
    elven: [
      '100-gold-elven-rpg-coins',
      '500-gold-elven-rpg-coins',
      '1000-gold-elven-rpg-coins',
    ],
    human: [
      '100-gold-human-rpg-coins',
      '500-gold-human-rpg-coins',
      '1000-gold-human-rpg-coins',
    ],
    fiendish: [
      '100-gold-fiendish-rpg-coins',
      '500-gold-fiendish-rpg-coins',
      '1000-gold-fiendish-rpg-coins',
    ],
    'mixed-gold': ['100-mixed-gold-rpg-coins', '500-mixed-gold-rpg-coins', '1000-mixed-gold-rpg-coins'],
    copper: ['100-copper-rpg-coins', '500-copper-rpg-coins', '1000-copper-rpg-coins'],
    silver: ['100-silver-rpg-coins', '500-silver-rpg-coins', '1000-silver-rpg-coins'],
    platinum: ['100-platinum-rpg-coins', '500-platinum-rpg-coins', '1000-platinum-rpg-coins'],
    electrum: ['100-electrum-rpg-coins', '500-electrum-rpg-coins', '1000-electrum-rpg-coins'],
  };

  const CHOICE_LABELS = {
    mixed: 'Mixed-metal coins',
    gold: 'Original gold coins',
    dwarven: 'Dwarven gold coins',
    elven: 'Elven gold coins',
    human: 'Human gold coins',
    fiendish: 'Fiendish gold coins',
    'mixed-gold': 'Mixed gold',
    copper: 'Copper coins',
    silver: 'Silver coins',
    platinum: 'Platinum coins',
    electrum: 'Electrum coins',
    gunmetal: 'Dark gunmetal ingots',
  };

  const INGOT_HANDLES = {
    mixed: '20-mixed-metal-rpg-ingots',
    gold: '20-gold-metal-rpg-ingots',
    silver: '20-silver-metal-rpg-ingots',
    copper: '20-copper-metal-rpg-ingots',
    platinum: '20-platinum-metal-rpg-ingots',
    gunmetal: '20-dark-gunmetal-rpg-ingots',
  };

  const BAR_HANDLES = {
    mixed: '40-mixed-metal-rpg-trade-bars',
    gold: '40-gold-metal-rpg-trade-bars',
    silver: '40-silver-metal-rpg-trade-bars',
    copper: '40-copper-metal-rpg-trade-bars',
    platinum: '40-platinum-metal-rpg-trade-bars',
  };

  class HoardBuilder extends HTMLElement {
    constructor() {
      super();
      this.productCache = new Map();
      this.recommendations = [];
      this.deselectedProducts = new Set();
      this.requestSequence = 0;
      this.updateTimer = null;
    }

    connectedCallback() {
      this.canvas = this.querySelector('[data-hoard-canvas]');
      this.loading = this.querySelector('[data-hoard-loading]');
      this.productGrid = this.querySelector('[data-hoard-products]');
      this.emptyState = this.querySelector('[data-hoard-empty]');
      this.addButton = this.querySelector('[data-hoard-add]');
      this.cartStatus = this.querySelector('[data-hoard-cart-status]');
      this.budgetInput = this.querySelector('[data-hoard-budget]');
      this.budgetStatus = this.querySelector('[data-hoard-budget-status]');
      this.currency = this.dataset.currency || 'AUD';
      this.locale = this.dataset.locale || document.documentElement.lang || 'en-AU';
      this.moneyFormatter = this.createMoneyFormatter();

      if (!this.initialised) {
        this.initialiseLineBuilders();
        this.restoreState();
        this.initialised = true;
      }
      this.bindEvents();
      this.syncPicker(true);
      this.syncCategoryControls();
      this.update({ immediate: true });
    }

    createMoneyFormatter() {
      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency,
          currencyDisplay: 'narrowSymbol',
        });
      } catch (error) {
        return new Intl.NumberFormat('en-AU', { style: 'currency', currency: this.currency });
      }
    }

    bindEvents() {
      this.events?.abort();
      this.events = new AbortController();
      const signal = this.events.signal;
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-hoard-picker-family], [data-hoard-picker-type]')) {
          this.syncPicker(event.target.matches('[data-hoard-picker-family]'));
          return;
        }
        if (event.target.matches('[data-hoard-country]')) {
          this.saveState();
          event.target.form?.submit();
          return;
        }

        if (event.target.matches('.hoard-builder__product-select')) {
          const key = event.target.dataset.productKey;
          if (event.target.checked) this.deselectedProducts.delete(key);
          else this.deselectedProducts.add(key);
          this.updateSelectedTotals();
          return;
        }

        if (event.target.matches('[data-hoard-enable]')) {
          this.syncCategoryControls();
        }

        if (event.target.matches('[data-hoard-type]')) {
          this.syncLineSizeAvailability(event.target.closest('[data-hoard-line]'));
        }

        if (event.target.matches('[data-hoard-quantity]')) {
          event.target.value = String(this.normaliseQuantity(event.target.value));
        }

        if (event.target.matches('[data-hoard-enable], [data-hoard-type], [data-hoard-size], [data-hoard-quantity]')) {
          this.saveState();
          this.update();
        }
      }, { signal });

      this.addEventListener('input', (event) => {
        if (!event.target.matches('[data-hoard-quantity]')) return;
        this.saveState();
        this.update({ delay: 180 });
      }, { signal });

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-hoard-picker-add]')) { this.addPickedTreasure(); return; }
        const addButton = event.target.closest('[data-hoard-add-line]');
        if (addButton) {
          const category = addButton.closest('[data-hoard-category]');
          const line = this.addLine(category, DEFAULT_LINES[category?.dataset.hoardCategory], true);
          if (line) this.commitLineChange();
          return;
        }

        const removeButton = event.target.closest('[data-hoard-remove-line]');
        if (removeButton) {
          const category = removeButton.closest('[data-hoard-category]');
          removeButton.closest('[data-hoard-line]')?.remove();
          if (!category.querySelector('[data-hoard-line]')) {
            category.querySelector('[data-hoard-enable]').checked = false;
            this.resetCategoryLines(category, []);
          }
          this.commitLineChange();
          this.querySelector('[data-hoard-picker-add]')?.focus();
          return;
        }

        const decreaseButton = event.target.closest('[data-hoard-quantity-decrease]');
        const increaseButton = event.target.closest('[data-hoard-quantity-increase]');
        const quantityButton = decreaseButton || increaseButton;
        if (quantityButton) {
          const input = quantityButton.closest('.hoard-builder__quantity-control')?.querySelector('[data-hoard-quantity]');
          if (!input) return;
          const change = increaseButton ? 1 : -1;
          input.value = String(this.normaliseQuantity(Number(input.value) + change));
          this.commitLineChange();
        }
      }, { signal });

      this.budgetInput?.addEventListener('input', () => {
        this.saveState();
        this.update({ delay: 240 });
      }, { signal });

      this.addButton?.addEventListener('click', () => this.addSelectedToCart(), { signal });
    }

    syncPicker(reset = false) {
      const family = this.querySelector('[data-hoard-picker-family]');
      if (!family) return;
      const template = this.querySelector(`[data-hoard-category="${family.value}"] [data-hoard-line-template]`);
      const type = this.querySelector('[data-hoard-picker-type]');
      const size = this.querySelector('[data-hoard-picker-size]');
      if (reset) {
        type.replaceChildren(...Array.from(template.content.querySelector('[data-hoard-type]').options, option => option.cloneNode(true)));
        size.replaceChildren(...Array.from(template.content.querySelector('[data-hoard-size]').options, option => option.cloneNode(true)));
      }
      for (const option of size.options) option.disabled = (option.dataset.unavailableFor || '').split(',').includes(type.value);
      if (size.selectedOptions[0]?.disabled) size.value = Array.from(size.options).find(option => !option.disabled)?.value || '';
      this.querySelector('[data-hoard-picker-add]').textContent = 'Add treasure';
      this.querySelector('[data-hoard-picker-feedback]').textContent = '';
    }

    addPickedTreasure() {
      const family = this.querySelector('[data-hoard-picker-family]');
      const category = this.querySelector(`[data-hoard-category="${family.value}"]`);
      const enable = category.querySelector('[data-hoard-enable]');
      const quantity = this.querySelector('[data-hoard-picker-quantity]');
      quantity.value = String(this.normaliseQuantity(quantity.value));
      const values = {type: this.querySelector('[data-hoard-picker-type]').value, size: this.querySelector('[data-hoard-picker-size]').value, quantity: Number(quantity.value)};
      const feedback = this.querySelector('[data-hoard-picker-feedback]');
      if (enable.checked && category.querySelectorAll('[data-hoard-line]').length >= 12) {
        feedback.textContent = 'You have 12 rows in this family. Increase an existing quantity or remove a row to add a different pack.';
        return;
      }
      if (enable.checked) this.addLine(category, values);
      else { this.resetCategoryLines(category, [values]); enable.checked = true; }
      this.commitLineChange();
      feedback.textContent = family.value === 'treasures' ? 'Treasure interest added.' : `${values.quantity} ${values.quantity === 1 ? 'pack' : 'packs'} added to your hoard.`;
    }

    initialiseLineBuilders() {
      this.querySelectorAll('[data-hoard-category]').forEach((category) => {
        this.addLine(category, DEFAULT_LINES[category.dataset.hoardCategory]);
      });
    }

    addLine(category, values = {}, focus = false) {
      const template = category?.querySelector('[data-hoard-line-template]');
      const container = category?.querySelector('[data-hoard-lines]');
      if (!template || !container) return null;

      const line = template.content.firstElementChild.cloneNode(true);
      const lineNumber = `${this.dataset.sectionId || 'builder'}-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 7)}`;
      line.dataset.hoardLineId = lineNumber;

      const type = line.querySelector('[data-hoard-type]');
      const size = line.querySelector('[data-hoard-size]');
      const quantity = line.querySelector('[data-hoard-quantity]');
      if (type && values?.type && Array.from(type.options).some((option) => option.value === values.type)) {
        type.value = values.type;
      }
      if (size && values?.size && Array.from(size.options).some((option) => option.value === values.size)) {
        size.value = values.size;
      }
      if (quantity) quantity.value = String(this.normaliseQuantity(values?.quantity));

      line.querySelectorAll('select, input').forEach((control, index) => {
        control.id = `HoardLine-${lineNumber}-${index}`;
        const label = control.closest('label');
        if (label) label.htmlFor = control.id;
      });

      container.append(line);
      this.syncLineSizeAvailability(line);
      this.syncLineButtons(category);
      if (focus) type?.focus();
      return line;
    }

    resetCategoryLines(category, lines) {
      const container = category?.querySelector('[data-hoard-lines]');
      if (!container) return;
      container.replaceChildren();
      const requestedLines = Array.isArray(lines) && lines.length ? lines : [DEFAULT_LINES[category.dataset.hoardCategory]];
      requestedLines.slice(0, 12).forEach((line) => this.addLine(category, line));
      this.syncLineButtons(category);
    }

    syncLineSizeAvailability(line) {
      if (!line) return;
      const type = line.querySelector('[data-hoard-type]')?.value;
      const size = line.querySelector('[data-hoard-size]');
      if (!size) return;

      Array.from(size.options).forEach((option) => {
        const unavailable = (option.dataset.unavailableFor || '').split(',').filter(Boolean);
        option.disabled = unavailable.includes(type);
      });

      if (size.selectedOptions[0]?.disabled) {
        const firstAvailable = Array.from(size.options).find((option) => !option.disabled);
        if (firstAvailable) size.value = firstAvailable.value;
      }
    }

    syncLineButtons(category) {
      if (!category) return;
      const lines = Array.from(category.querySelectorAll('[data-hoard-line]'));
      const enabled = category.querySelector('[data-hoard-enable]')?.checked || false;
      lines.forEach((line) => {
        const remove = line.querySelector('[data-hoard-remove-line]');
        if (remove) remove.disabled = !enabled;
      });
      const add = category.querySelector('[data-hoard-add-line]');
      if (add) add.disabled = !enabled || lines.length >= 12;
    }

    commitLineChange() {
      this.syncCategoryControls();
      this.saveState();
      this.update();
    }

    normaliseQuantity(value) {
      const quantity = Number.parseInt(value, 10);
      if (!Number.isFinite(quantity)) return 1;
      return Math.min(1000, Math.max(1, quantity));
    }

    getState() {
      const categories = {};

      this.querySelectorAll('[data-hoard-category]').forEach((category) => {
        const key = category.dataset.hoardCategory;
        const enabled = category.querySelector('[data-hoard-enable]')?.checked || false;
        const lines = Array.from(category.querySelectorAll('[data-hoard-line]')).map((line) => ({
          type: line.querySelector('[data-hoard-type]')?.value || DEFAULT_LINES[key]?.type,
          size: line.querySelector('[data-hoard-size]')?.value || DEFAULT_LINES[key]?.size || 'small',
          quantity: this.normaliseQuantity(line.querySelector('[data-hoard-quantity]')?.value),
        }));
        categories[key] = { enabled, lines };
      });

      const rawBudget = this.budgetInput?.value.trim() || '';
      const parsedBudget = Number.parseFloat(rawBudget);

      return {
        categories,
        budget: Number.isFinite(parsedBudget) && parsedBudget > 0 ? parsedBudget : null,
        budgetInput: rawBudget,
        country: this.dataset.country,
      };
    }

    saveState() {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.getState()));
      } catch (error) {
        // Storage is an enhancement; the builder remains fully usable without it.
      }
    }

    restoreState() {
      let saved;
      try {
        saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
      } catch (error) {
        return;
      }

      if (!saved?.categories) return;

      Object.entries(saved.categories).forEach(([key, value]) => {
        const category = this.querySelector(`[data-hoard-category="${key}"]`);
        if (!category) return;

        const enable = category.querySelector('[data-hoard-enable]');
        if (enable) enable.checked = Boolean(value.enabled);
        this.resetCategoryLines(category, value.lines);
      });

      if (this.budgetInput && typeof saved.budgetInput === 'string') {
        this.budgetInput.value = saved.budgetInput;
      }
    }

    syncCategoryControls() {
      this.querySelectorAll('[data-hoard-category]').forEach((category) => {
        const enabled = category.querySelector('[data-hoard-enable]')?.checked || false;
        const builder = category.querySelector('[data-hoard-pack-builder]');

        category.hidden = !enabled;
        category.classList.toggle('is-selected', enabled);
        if (builder) builder.hidden = !enabled;
        category.querySelectorAll('[data-hoard-line] select, [data-hoard-line] input, [data-hoard-line] button').forEach((control) => {
          const permanentlyFixed = control.matches('[data-hoard-fixed-control]');
          control.disabled = !enabled || permanentlyFixed;
        });
        const addButton = category.querySelector('[data-hoard-add-line]');
        if (addButton) addButton.disabled = !enabled;
        this.syncLineButtons(category);
      });
    }

    update({ immediate = false, delay = 0 } = {}) {
      clearTimeout(this.updateTimer);
      const run = () => this.refreshRecommendations();
      if (immediate || delay === 0) run();
      else this.updateTimer = setTimeout(run, delay);
    }

    async refreshRecommendations() {
      const sequence = ++this.requestSequence;
      const state = this.getState();
      const groups = this.buildProductGroups(state);

      this.setLoading(true);
      this.cartStatus.textContent = '';

      if (groups.length === 0) {
        this.recommendations = [];
        this.renderRecommendations(state, { message: 'Choose a treasure pack above and add it to your hoard.' });
        this.setLoading(false);
        return;
      }

      try {
        await this.resolveGroups(groups);
        if (sequence !== this.requestSequence) return;

        const result = this.chooseWithinBudget(groups, state.budget);
        const previews = groups.flatMap((group) => group.preview ? [group.preview] : []);
        this.recommendations = this.aggregateRecommendations([...result.choices, ...previews]);
        if (previews.length) result.message += ' Preview-only packs stay in your hoard, outside the cart total and budget.';
        this.renderRecommendations(state, result);
      } catch (error) {
        if (sequence !== this.requestSequence) return;
        this.recommendations = [];
        this.renderRecommendations(state, {
          message: 'We could not load live catalogue details. Please refresh and try again.',
          error: true,
        });
      } finally {
        if (sequence === this.requestSequence) this.setLoading(false);
      }
    }

    buildProductGroups(state) {
      const groups = [];
      const categories = state.categories;

      if (categories.coins?.enabled) {
        categories.coins.lines.forEach((line) => {
          const tier = SIZE_INDEX[line.size] ?? 0;
          const handle = COIN_HANDLES[line.type]?.[tier];
          if (!handle) return;
          groups.push(
            this.exactPackGroup({
            category: 'Coins',
              label: `${CHOICE_LABELS[line.type] || `${line.type} coins`} · ${SIZE_LABELS[line.size]} pack`,
              handle,
              quantity: line.quantity,
              piecesPerPack: [100, 500, 1000][tier],
              size: line.size,
            })
          );
        });
      }

      if (categories.gems?.enabled) {
        categories.gems.lines.forEach((line) => {
          groups.push(
            this.exactPackGroup({
            category: 'Gemstones',
              label: 'Mixed glass gems & pearls · small pack',
            handle: '100-fantasy-gem-pearl-rpg-tokens',
            piecesPerPack: 100,
              quantity: line.quantity,
              size: line.size,
            })
          );
        });
      }

      if (categories.ingots?.enabled) {
        categories.ingots.lines.forEach((line) => {
          const handle = INGOT_HANDLES[line.type];
          if (!handle) return;
          groups.push(
            this.exactPackGroup({
              category: 'Ingots',
              label: `${line.type === 'gunmetal' ? 'Dark gunmetal' : this.capitalise(line.type)} ingots · small pack`,
              handle,
              piecesPerPack: 20,
              quantity: line.quantity,
              size: line.size,
            })
          );
        });
      }

      if (categories['trade-bars']?.enabled) {
        categories['trade-bars'].lines.forEach((line) => {
          const handle = BAR_HANDLES[line.type];
          if (!handle) return;
          groups.push(
            this.exactPackGroup({
              category: 'Trade bars',
              label: `${this.capitalise(line.type)} trade bars · small pack`,
              handle,
              piecesPerPack: 40,
              quantity: line.quantity,
              size: line.size,
            })
          );
        });
      }

      return groups;
    }

    exactPackGroup({ category, label, handle, piecesPerPack, quantity, size, concept = false }) {
      const packQuantity = Math.max(1, Math.round(Number(quantity) || 1));
      return {
        category,
        label,
        desiredTier: packQuantity - 1,
        concept,
        options: Array.from({ length: packQuantity }, (_, tier) => ({
          handle,
          quantity: tier + 1,
          pieces: piecesPerPack * (tier + 1),
          tier,
          requestedSize: size,
        })),
      };
    }

    async resolveGroups(groups) {
      const handles = [...new Set(groups.flatMap((group) => group.options.map((option) => option.handle)))];
      // One missing listing must not erase the other requested products.
      const responses = await Promise.allSettled(handles.map((handle) => this.fetchProduct(handle)));
      const products = new Map(handles.map((handle, index) => [handle, responses[index]]));
      const needsPreview = responses.some((response) => response.status === 'rejected' ||
        !response.value?.available || !response.value.variants?.some((variant) => variant.available));
      const packs = needsPreview ? await this.fetchPackIndex().catch(() => new Map()) : new Map();

      groups.forEach((group) => {
        const requested = group.options.find((option) => option.tier === group.desiredTier);
        const response = products.get(requested?.handle);
        const product = response?.status === 'fulfilled' ? response.value : null;
        const variant = product?.available && product.variants?.find((item) => item.available);
        if (requested && !variant) {
          const pack = packs.get(requested.handle);
          // Saved identities are only for the illustration, never a stock or price fallback.
          if (pack) group.preview = {
            ...requested,
            category: group.category,
            groupLabel: group.label,
            product: { title: product?.title || pack.title },
            variant: { id: Number(pack.variantId), sku: pack.sku },
            pieces: pack.piecesPerPack * requested.quantity,
            price: 0,
            previewOnly: true,
            availability: product ? 'Sold out' : response?.reason?.status === 404
              ? 'Unavailable in this market' : 'Availability could not be checked',
          };
        }
        const seen = new Set();
        group.options = group.options
          .map((option) => {
            if (!variant) return null;
            const key = `${variant.id}:${option.quantity}:${option.tier}`;
            if (seen.has(key)) return null;
            seen.add(key);
            return {
              ...option,
              category: group.category,
              groupLabel: group.label,
              concept: Boolean(group.concept),
              product,
              variant,
              unitPrice: variant.price,
              price: variant.price * option.quantity,
            };
          })
          .filter(Boolean)
          .sort(
            (a, b) =>
              a.tier - b.tier || Number(a.fallback) - Number(b.fallback) || a.price - b.price
          );
      });
    }

    async fetchPackIndex() {
      if (!this.packIndexReady) {
        const urls = JSON.parse(this.querySelector('[data-hoard-art-assets]').textContent);
        this.packIndexReady = fetch(urls.packs).then(async (response) => {
          if (!response.ok) throw new Error('Treasure recipes could not be loaded.');
          const registry = await response.json();
          return new Map(Object.values(registry.packs).map((pack) => [pack.handle, pack]));
        }).catch((error) => { this.packIndexReady = null; throw error; });
      }
      return this.packIndexReady;
    }

    async fetchProduct(handle) {
      if (this.productCache.has(handle)) return this.productCache.get(handle);
      const root = window.Shopify?.routes?.root || '/';
      const url = `${root}products/${encodeURIComponent(handle)}.js`;
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) {
        const error = new Error(`Product request failed: ${response.status}`);
        error.status = response.status;
        throw error;
      }
      const product = await response.json();
      this.productCache.set(handle, product);
      return product;
    }

    chooseWithinBudget(groups, budgetMajorUnits) {
      const productGroups = groups.filter((group) => !group.concept);
      const availableGroups = productGroups.filter((group) => group.options.length);
      const unavailableCount = productGroups.length - availableGroups.length;

      if (productGroups.length === 0) return { choices: [], message: '' };
      const budget = budgetMajorUnits === null ? null : Math.round(budgetMajorUnits * 100);

      if (availableGroups.length === 0) {
        return { choices: [], message: 'No matching products are currently available.' };
      }

      if (budget === null) {
        const choices = availableGroups.map((group) => this.desiredOption(group));
        return {
          choices,
          unavailableCount,
          message: unavailableCount
            ? 'Some requested packs are unavailable to buy. Available packs are matched exactly.'
            : 'Your chosen packs are ready. Set a budget if you’d like to limit the total.',
        };
      }

      let choices = availableGroups.map((group) => this.lowestOption(group));
      let total = choices.reduce((sum, option) => sum + option.price, 0);

      if (total <= budget) {
        let upgraded = true;
        while (upgraded) {
          upgraded = false;
          const candidates = choices
            .map((choice, index) => {
              const group = availableGroups[index];
              const next = this.nextOption(group, choice);
              return next ? { index, next, delta: next.price - choice.price } : null;
            })
            .filter((candidate) => candidate && candidate.delta >= 0)
            .sort((a, b) => a.delta - b.delta);

          const fitting = candidates.find((candidate) => total + candidate.delta <= budget);
          if (fitting) {
            choices[fitting.index] = fitting.next;
            total += fitting.delta;
            upgraded = true;
          }
        }

        const scaledDown = choices.some(
          (choice, index) => choice.tier < availableGroups[index].desiredTier
        );
        return {
          choices,
          unavailableCount,
          message: scaledDown
            ? `This mix stays within your ${this.formatMoney(
                budget
              )} budget; some pack quantities were reduced to fit.`
            : `This mix stays within your ${this.formatMoney(budget)} budget.`,
        };
      }

      const affordable = [];
      let runningTotal = 0;
      const baselines = availableGroups
        .map((group) => this.lowestOption(group))
        .sort((a, b) => a.price - b.price);

      baselines.forEach((option) => {
        if (runningTotal + option.price <= budget) {
          affordable.push(option);
          runningTotal += option.price;
        }
      });

      if (affordable.length) {
        return {
          choices: affordable,
          constrained: true,
          message: `Your budget cannot include every selected pack line; these exact selections fit under ${this.formatMoney(
            budget
          )}.`,
        };
      }

      const cheapest = baselines[0];
      return {
        choices: [cheapest],
        constrained: true,
        overBudget: true,
        message: `The closest available match is ${this.formatMoney(
          cheapest.price
        )}, which is above your ${this.formatMoney(budget)} budget.`,
      };
    }

    desiredOption(group) {
      const tiers = group.options.filter((option) => option.tier <= group.desiredTier);
      if (tiers.length) return tiers[tiers.length - 1];
      return group.options[0];
    }

    lowestOption(group) {
      return [...group.options].sort((a, b) => a.price - b.price || a.tier - b.tier)[0];
    }

    nextOption(group, current) {
      return group.options.find(
        (option) => option.tier > current.tier && option.tier <= group.desiredTier
      );
    }

    aggregateRecommendations(choices) {
      const products = new Map();

      choices.forEach((choice) => {
        if (!choice) return;
        const key = String(choice.variant.id);
        const existing = products.get(key);
        if (existing) {
          existing.quantity += choice.quantity;
          existing.pieces += choice.pieces;
          existing.price += choice.price;
          if (!existing.labels.includes(choice.groupLabel)) existing.labels.push(choice.groupLabel);
          if (!existing.categories.includes(choice.category)) existing.categories.push(choice.category);
          existing.concept = existing.concept || choice.concept;
        } else {
          products.set(key, {
            key,
            handle: choice.handle,
            product: choice.product,
            variant: choice.variant,
            quantity: choice.quantity,
            pieces: choice.pieces,
            price: choice.price,
            labels: [choice.groupLabel],
            categories: [choice.category],
            fallback: choice.fallback,
            concept: choice.concept,
            previewOnly: Boolean(choice.previewOnly),
            availability: choice.availability,
          });
        }
      });

      return [...products.values()];
    }

    renderRecommendations(state, result) {
      this.productGrid.replaceChildren();
      this.emptyState.hidden = this.recommendations.length > 0;
      this.budgetStatus.textContent = result.message || '';

      this.recommendations.forEach((recommendation) => {
        this.productGrid.append(this.createProductCard(recommendation));
      });

      this.updateSelectedTotals();
      this.updateLeadFields(state);
    }

    createProductCard(recommendation) {
      const article = document.createElement('article');
      article.className = 'hoard-builder__product-card';

      const selectWrap = document.createElement('label');
      selectWrap.className = 'hoard-builder__product-select-wrap';
      selectWrap.title = 'Include this product';
      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.className = 'hoard-builder__product-select';
      checkbox.dataset.productKey = recommendation.key;
      checkbox.checked = !this.deselectedProducts.has(recommendation.key);
      const checkboxLabel = document.createElement('span');
      checkboxLabel.className = 'visually-hidden';
      checkboxLabel.textContent = `Include ${recommendation.product.title}`;
      selectWrap.append(checkbox, checkboxLabel);

      if (recommendation.previewOnly) {
        article.classList.add('hoard-builder__product-card--preview');
        selectWrap.title = 'Include in the visual hoard';
        checkboxLabel.textContent = `Preview ${recommendation.product.title}`;
        const body = document.createElement('div');
        body.className = 'hoard-builder__product-body';
        const status = document.createElement('p');
        status.className = 'hoard-builder__product-kicker';
        status.textContent = `${recommendation.availability} · Preview only`;
        const title = document.createElement('h3');
        title.className = 'hoard-builder__product-title';
        title.textContent = recommendation.product.title;
        const detail = document.createElement('p');
        detail.textContent = `${recommendation.quantity} ${recommendation.quantity === 1 ? 'pack' : 'packs'} · ${recommendation.pieces.toLocaleString(this.locale)} pieces. Shown in your hoard; not included in the cart total or budget.`;
        body.append(status, title, detail);
        article.append(selectWrap, body);
        return article;
      }

      const link = document.createElement('a');
      link.className = 'hoard-builder__product-image';
      link.href = this.productUrl(recommendation.handle);
      const image = document.createElement('img');
      image.loading = 'lazy';
      image.width = 480;
      image.height = 360;
      image.alt = recommendation.product.title;
      image.src = recommendation.product.featured_image || '';
      link.append(image);

      const body = document.createElement('div');
      body.className = 'hoard-builder__product-body';
      const kicker = document.createElement('p');
      kicker.className = 'hoard-builder__product-kicker';
      kicker.textContent = recommendation.concept
        ? 'Closest available match'
        : recommendation.categories.join(' · ');
      const title = document.createElement('h3');
      const titleLink = document.createElement('a');
      titleLink.className = 'hoard-builder__product-title';
      titleLink.href = this.productUrl(recommendation.handle);
      titleLink.textContent = recommendation.product.title;
      title.append(titleLink);

      const meta = document.createElement('div');
      meta.className = 'hoard-builder__product-meta';
      const quantity = document.createElement('span');
      quantity.textContent = `${recommendation.quantity} ${recommendation.quantity === 1 ? 'pack' : 'packs'} · ${recommendation.pieces.toLocaleString(
        this.locale
      )} pieces`;
      const price = document.createElement('strong');
      price.textContent = this.formatMoney(recommendation.price);
      meta.append(quantity, price);
      body.append(kicker, title, meta);
      article.append(selectWrap, link, body);
      return article;
    }

    updateSelectedTotals() {
      const selected = this.getSelectedRecommendations();
      const purchasable = this.getPurchasableRecommendations();
      const total = purchasable.reduce((sum, item) => sum + item.price, 0);
      const pieces = selected.reduce((sum, item) => sum + item.pieces, 0);
      const requestedPacks = Object.values(this.getState().categories)
        .filter((category) => category.enabled)
        .flatMap((category) => category.lines)
        .reduce((sum, line) => sum + line.quantity, 0);

      this.querySelector('[data-hoard-selected-total]').textContent = this.formatMoney(total);
      this.querySelector('[data-hoard-total]').textContent = this.formatMoney(total);
      this.querySelector('[data-hoard-piece-count]').textContent = pieces.toLocaleString(this.locale);
      this.querySelector('[data-hoard-interest-count]').textContent = String(requestedPacks);
      this.addButton.disabled = purchasable.length === 0;
      const previewPieces = selected.filter((item) => item.previewOnly).reduce((sum, item) => sum + item.pieces, 0);
      const stockStatus = this.querySelector('[data-hoard-stock-status]');
      if (stockStatus) stockStatus.textContent = previewPieces
        ? `${previewPieces.toLocaleString(this.locale)} pieces are preview only. These packs are unavailable to buy and are excluded from the cart total and budget.` : '';
      this.updateLeadFields(this.getState());
      this.renderSelection();
    }

    getSelectedRecommendations() {
      return this.recommendations.filter((item) => !this.deselectedProducts.has(item.key));
    }

    getPurchasableRecommendations() {
      return this.getSelectedRecommendations().filter((item) => !item.previewOnly &&
        item.product.available && item.variant.available);
    }

    updateLeadFields(state) {
      const enabled = Object.entries(state.categories)
        .filter(([, category]) => category.enabled)
        .flatMap(([key, category]) =>
          category.lines.map((line) => {
            const type = CHOICE_LABELS[line.type] || this.capitalise(line.type.replaceAll('-', ' '));
            return `${key}: ${line.quantity} × ${SIZE_LABELS[line.size] || line.size} ${type}`;
          })
        );
      const selected = this.getSelectedRecommendations();
      const products = selected.map(
        (item) => `${item.quantity} × ${item.product.title} (${item.previewOnly ? `${item.availability}; preview only` : this.formatMoney(item.price)})`
      );

      const selectionField = this.querySelector('[data-hoard-lead-selection]');
      const budgetField = this.querySelector('[data-hoard-lead-budget]');
      const productsField = this.querySelector('[data-hoard-lead-products]');
      if (selectionField) selectionField.value = enabled.join('; ') || 'No pack lines selected';
      if (budgetField) {
        budgetField.value = state.budget
          ? `${this.formatMoney(Math.round(state.budget * 100))} ${this.currency}`
          : `No budget · ${this.currency}`;
      }
      if (productsField) productsField.value = products.join('; ') || 'No products selected';
    }

    setLoading(isLoading) {
      this.loading.hidden = !isLoading;
      this.productGrid.toggleAttribute('aria-busy', isLoading);
    }

    formatMoney(cents) {
      return this.moneyFormatter.format((Number(cents) || 0) / 100);
    }

    productUrl(handle) {
      const root = window.Shopify?.routes?.root || '/';
      return `${root}products/${encodeURIComponent(handle)}`;
    }

    capitalise(value) {
      return value.charAt(0).toUpperCase() + value.slice(1);
    }

    async addSelectedToCart() {
      const selected = this.getPurchasableRecommendations();
      if (!selected.length || this.addButton.disabled) return;

      this.addButton.disabled = true;
      this.addButton.setAttribute('aria-disabled', 'true');
      this.cartStatus.textContent = 'Adding your selected matches…';

      try {
        const response = await fetch(this.dataset.cartAddUrl, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items: selected.map((item) => ({ id: item.variant.id, quantity: item.quantity })),
          }),
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || 'The selected products could not be added.');
        }

        document.dispatchEvent(new CustomEvent('muck:cart-success'));
        await this.updateCartBubble();
        const packCount = selected.reduce((sum, item) => sum + item.quantity, 0);
        this.cartStatus.textContent = `${packCount} ${packCount === 1 ? 'pack' : 'packs'} added. Your cart is ready to review.`;
      } catch (error) {
        this.cartStatus.textContent = error.message || 'Something went wrong. Please try again.';
      } finally {
        this.addButton.removeAttribute('aria-disabled');
        this.addButton.disabled = this.getPurchasableRecommendations().length === 0;
      }
    }

    async updateCartBubble() {
      const response = await fetch(`${this.dataset.cartUrl}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;
      const cart = await response.json();
      const cartLink = document.getElementById('cart-icon-bubble');
      if (!cartLink) return;

      let bubble = cartLink.querySelector('.cart-count-bubble');
      if (!bubble) {
        bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';
        bubble.innerHTML = '<span aria-hidden="true"></span><span class="visually-hidden"></span>';
        cartLink.append(bubble);
      }
      const visibleCount = bubble.querySelector('[aria-hidden="true"]');
      const accessibleCount = bubble.querySelector('.visually-hidden');
      if (visibleCount) visibleCount.textContent = cart.item_count < 100 ? cart.item_count : '';
      if (accessibleCount) accessibleCount.textContent = `${cart.item_count} items`;
    }

    hoardScore() {
      return this.getSelectedRecommendations().reduce((n, item) => n + item.pieces, 0) / 100;
    }

    async renderSelection() {
      const sequence = this.artSequence = (this.artSequence || 0) + 1;
      this.querySelector('muck-sprite')?.setHoard?.(this.hoardScore());
      const selected = this.getSelectedRecommendations();
      const accessible = this.querySelector('[data-hoard-accessible-summary]');
      if (accessible) accessible.textContent = `${selected.reduce((n, item) => n + item.pieces, 0).toLocaleString(this.locale)} pieces in your visual hoard.`;
      if (!this.dataset.artModule) return;
      try {
        if (!this.artReady) {
          this.artReady = import(this.dataset.artModule).then(({ ProductHoard }) => ProductHoard.create({
            canvas: this.canvas,
            denCanvas: this.querySelector('[data-hoard-den]'),
            dragonCanvas: this.querySelector('[data-hoard-dragon]'),
            status: this.querySelector('[data-hoard-art-status]'),
            dragonStatus: this.querySelector('[data-hoard-dragon-status]'),
            pause: this.querySelector('[data-hoard-dragon-pause]'),
            replay: this.querySelector('[data-hoard-dragon-replay]'),
            urls: JSON.parse(this.querySelector('[data-hoard-art-assets]').textContent),
          })).then(view => { this.art = view; if (!this.isConnected) view.dispose(); return view; });
        }
        const view = await this.artReady;
        if (!this.isConnected || sequence !== this.artSequence) return;
        view.setSelection(selected.map(item => ({sku: item.variant.sku, variantId: item.variant.id, quantity: item.quantity})));
      } catch (error) {
        this.art?.fail();
        const status = this.querySelector('[data-hoard-art-status]');
        if (status) status.textContent = 'The illustration is unavailable. Your selected products and quantities are retained.';
      }
    }

    disconnectedCallback() {
      this.events?.abort();
      clearTimeout(this.updateTimer);
      this.requestSequence++;
      this.artSequence = (this.artSequence || 0) + 1;
      this.art?.dispose();
      this.artReady = null;
    }

  }

  if (!customElements.get('hoard-builder')) {
    customElements.define('hoard-builder', HoardBuilder);
  }
})();
